import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import { emailService } from '../../utils/emailService';

const EmailGateModal = ({ isOpen, onClose, onSuccess, title = "Access Financial Projections" }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    
    if (!email?.trim()) {
      setError('Email address is required');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await emailService?.submitEmailForAccess(email);
      
      if (result?.success) {
        setIsSuccess(true);
        // Show success message briefly then proceed
        setTimeout(() => {
          onSuccess?.(email, result?.isExisting);
          onClose?.();
        }, 1500);
      } else {
        setError(result?.message || 'Failed to submit email. Please try again.');
      }
    } catch (error) {
      setError('Failed to submit email. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-card border border-border rounded-lg max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="Lock" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-foreground">{title}</h2>
              <p className="text-sm text-muted-foreground">Enter your email to unlock</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isSuccess ? (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mx-auto">
                <Icon name="Check" size={32} color="var(--color-success)" />
              </div>
              <h3 className="text-lg font-medium text-foreground">Email Verified!</h3>
              <p className="text-muted-foreground">
                Redirecting to your financial projections...
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Value Proposition */}
              <div className="text-center space-y-3">
                <Icon name="BarChart3" size={48} color="var(--color-primary)" className="mx-auto" />
                <h3 className="text-xl font-heading font-bold text-foreground">
                  Get Your Comprehensive 5-Year Financial Analysis
                </h3>
                <p className="text-muted-foreground">
                  Unlock detailed projections, strategic recommendations, and actionable insights 
                  tailored to your RV park investment.
                </p>
              </div>

              {/* Features List */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-foreground mb-3">What you'll get:</h4>
                <div className="space-y-2">
                  {[
                    'Detailed 5-year revenue projections',
                    'Strategic growth recommendations', 
                    'Market analysis and benchmarks',
                    'Operational optimization strategies',
                    'Export-ready financial reports'
                  ]?.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Icon name="Check" size={16} color="var(--color-success)" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Email Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    disabled={isSubmitting}
                    className="w-full"
                    autoFocus
                  />
                  {error && (
                    <p className="text-destructive text-sm mt-2 flex items-center space-x-2">
                      <Icon name="AlertCircle" size={16} />
                      <span>{error}</span>
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !email?.trim()}
                  className="w-full"
                  iconName={isSubmitting ? 'Loader2' : 'Unlock'}
                  iconPosition="left"
                >
                  {isSubmitting ? 'Verifying Email...' : 'Unlock Financial Projections'}
                </Button>
              </form>

              {/* Trust Signals */}
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Shield" size={14} />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Mail" size={14} />
                    <span>No Spam</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>Instant Access</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  We respect your privacy. Your email is secure and will only be used to provide 
                  access to your personalized financial projections.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailGateModal;